import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { splitProductName } from "@/lib/productName";
import { getEffectiveStock, stockLabel } from "@/lib/stock";
import { api } from "@/lib/api";
import NotifyForm from "@/components/NotifyForm";
import VialKitAccessoryToggle from "./VialKitAccessoryToggle";
import AddonToggle from "./AddonToggle";
import BlendIngredientChip from "./BlendIngredientChip";

// Per-slug PDP header overrides. Currently only BPC-157 (Part 4 SEO pass)
//, narrowly scoped by design so each priority compound can be optimised
// individually without leaking copy across products. Add entries here for
// the next compound only when its own SEO task authorises them.
const PDP_HEADER_OVERRIDES = {
  "bpc-157": {
    category: "Research Peptide",
    h1: "BPC-157 Research Peptide",
    subtitle: "Lyophilised BPC-157 peptide supplied for laboratory research.",
  },
  "klow": {
    category: "Research Peptide Blend",
    h1: "KLOW Research Peptide Blend",
    subtitle: "Four-component peptide blend supplied for laboratory research.",
  },
  "klow-pen": {
    h1: "KLOW Research Peptide Pen Kit",
    subtitle:
      "Pre-filled KLOW peptide blend supplied in a dial-controlled pen format for laboratory research.",
  },
  "glow": {
    category: "Research Peptide Blend",
    h1: "GLOW Research Peptide Blend",
    subtitle: "Three-component peptide blend supplied for laboratory research.",
  },
  "glow-pen": {
    h1: "GLOW Research Peptide Pen Kit",
    subtitle:
      "Pre-filled GLOW peptide blend supplied in a dial-controlled pen format for laboratory research.",
  },
  "ghk-cu": {
    category: "Copper Peptide",
    h1: "GHK-Cu Copper Peptide",
    subtitle: "Lyophilised GHK-Cu copper peptide supplied for laboratory research.",
  },
  "kpv": {
    category: "Research Peptide",
    h1: "KPV Research Peptide",
    subtitle: "Lyophilised KPV tripeptide supplied for laboratory research.",
  },
  "semax": {
    category: "Research Peptide",
    h1: "Semax Research Peptide",
    subtitle: "Lyophilised Semax heptapeptide supplied for laboratory research.",
  },
  "nad-plus": {
    h1: "NAD+ Raw Vial",
    subtitle: "Nicotinamide adenine dinucleotide dry powder. Requires reconstitution.",
  },
  "nad-plus-pen": {
    h1: "NAD+ 500mg Pen Kit",
    subtitle: "Pre-filled NAD+ research format.",
  },
  "cjc-1295-no-dac": {
    category: "Research Peptide",
    h1: "CJC-1295 No DAC Research Peptide",
    subtitle: "Lyophilised CJC-1295 No DAC research peptide supplied for laboratory research.",
  },
  "cjc-1295-dac": {
    category: "Research Peptide",
    h1: "CJC-1295 DAC Research Peptide",
    subtitle: "Lyophilised CJC-1295 DAC research peptide supplied for laboratory research.",
  },
  "hulk": {
    category: "Research Peptide Blend",
    h1: "HULK Research Peptide Blend",
    subtitle: "Two-component research peptide blend containing CJC-1295 No DAC and Ipamorelin.",
  },
  "hulk-pen": {
    category: "Research Peptide Blend",
    h1: "HULK Research Peptide Pen Kit",
    subtitle: "Pre-filled HULK research blend containing CJC-1295 No DAC and Ipamorelin.",
  },
  "pt-141": {
    category: "Research Peptide",
    h1: "PT-141 (Bremelanotide) Research Peptide",
    subtitle: "Lyophilised PT-141 (bremelanotide) supplied for laboratory research.",
  },
  "ipamorelin": {
    category: "Research Peptide",
    h1: "Ipamorelin Research Peptide",
    subtitle: "Lyophilised ipamorelin pentapeptide supplied for laboratory research.",
  },
  "tb-500": {
    category: "Research Peptide",
    h1: "TB-500 Research Peptide",
    subtitle: "Lyophilised TB-500 peptide fragment supplied for laboratory research.",
  },
  "selank": {
    category: "Research Peptide",
    h1: "Selank Research Peptide",
    subtitle: "Lyophilised Selank heptapeptide supplied for laboratory research.",
  },
  "cagrilintide": {
    category: "Metabolic Research",
    h1: "Cagrilintide Research Peptide",
    subtitle: "Lyophilised cagrilintide, a long-acting amylin analogue, supplied for laboratory research.",
  },
  "mots-c": {
    category: "Mitochondrial Peptide",
    h1: "MOTS-C Research Peptide",
    subtitle: "Lyophilised MOTS-C mitochondrial-derived peptide supplied for laboratory research.",
  },
  "aod-9604": {
    category: "Research Peptide",
    h1: "AOD-9604 Research Peptide",
    subtitle: "Lyophilised AOD-9604 peptide fragment supplied for laboratory research.",
  },
  "wolverine": {
    category: "Research Blend",
    h1: "Wolverine Research Peptide Blend",
    subtitle: "Two-component BPC-157 and TB-500 peptide blend supplied for laboratory research.",
  },
  "wolverine-pen": {
    category: "Research Blend",
    h1: "Wolverine Research Peptide Pen Kit",
    subtitle: "Pre-filled BPC-157 and TB-500 research blend supplied in a dial-controlled pen format for laboratory research.",
  },
  "melanotan-1": {
    category: "Melanocortin Peptide",
    h1: "Melanotan I (Melanotan 1) Research Peptide",
    subtitle: "Lyophilised Melanotan I melanocortin peptide supplied for laboratory research.",
  },
  "melanotan-2": {
    category: "Melanocortin Peptide",
    h1: "Melanotan II (Melanotan 2) Research Peptide",
    subtitle: "Lyophilised Melanotan II cyclic melanocortin peptide supplied for laboratory research.",
  },
  "dsip": {
    category: "Research Peptide",
    h1: "DSIP Research Peptide",
    subtitle: "Lyophilised delta sleep-inducing peptide supplied for laboratory research.",
  },
  "epithalon": {
    category: "Tetrapeptide",
    h1: "Epithalon (Epitalon) Research Peptide",
    subtitle: "Lyophilised Epithalon (Epitalon) AEDG tetrapeptide supplied for laboratory research.",
  },
};

// Right column of the compound PDP, title, blend pills, CAS/LOT/HPLC line,
// strength selector, price, stock indicator, qty stepper, CTAs, kit
// contents (vial kits only), FAQ accordion, and the verify-your-product
// link. All behaviour lifted verbatim from the previous inline block in
// ProductPage, no logic change.
export default function CompoundProductDetails({ product, qty, setQty, variantIdx, setVariantIdx }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();

  const saving = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  // Per-PDP add-on modifiers. Raw Vial pages get the Recon Kit toggle,
  // Pen Kit pages get Travel Case + Sharps Bin. Both roll into the cart
  // as line-item modifiers (same cart line as the base product).
  const [reconAddon, setReconAddon] = useState(null);
  const [travelCaseAddon, setTravelCaseAddon] = useState(null);
  const [sharpsAddon, setSharpsAddon] = useState(null);

  const selectedAddons = [reconAddon, travelCaseAddon, sharpsAddon].filter(Boolean);

  const strengthVariants = Array.isArray(product.variants) ? product.variants : [];
  const activeStrength = strengthVariants[variantIdx] || null;
  const displayedPrice = activeStrength ? activeStrength.price : product.price;
  const activeStock = getEffectiveStock(
    product,
    activeStrength ? activeStrength.pack_size : null
  );
  const stockChip = stockLabel(activeStock);
  const isOOS = activeStock === 0;
  const canIncrease = activeStock === null || qty < activeStock;

  const addToCart = () => {
    if (isOOS) return;
    if (activeStrength) addItem(product, qty, activeStrength, selectedAddons);
    else addItem(product, qty, null, selectedAddons);
  };
  const buyNow = () => {
    if (isOOS) return;
    addToCart();
    navigate("/checkout");
  };

  const isBlend = product.cas_number?.includes("mg");
  // Prefer the structured `composition` field written by seed_data.py
  // (single source of truth for ingredient → PDP slug). Fall back to
  // parsing the legacy cas_number string for backwards-compatibility on
  // any blend that hasn't had a composition array assigned yet, in
  // that case slugs are unknown so every chip renders as plain text.
  const structuredComposition = Array.isArray(product.composition) ? product.composition : null;
  const parsedComposition = isBlend && !structuredComposition
    ? product.cas_number
        .split("·")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((raw) => {
          const m = raw.match(/^(.*?)\s+([\d.]+\s*(?:mg|mcg|iu|IU))\s*$/i);
          return m
            ? { name: m[1].trim(), amount: m[2].trim(), slug: null }
            : { name: raw, amount: "", slug: null };
        })
    : null;
  const blendComposition = structuredComposition || parsedComposition;

  const { main, sub } = splitProductName(product.name);
  const headerOverride = PDP_HEADER_OVERRIDES[product.slug] || null;
  const displayCategory = headerOverride?.category || (product.categories?.join(" · "));
  const displayH1 = headerOverride?.h1 || main;
  const displaySubtitle = headerOverride?.subtitle || product.short_description;

  return (
    <div>
      <p className="label-mono text-brandblue">{displayCategory}</p>
      <h1 className="mt-3 font-heading text-3xl font-black tracking-tight text-ink sm:text-4xl" data-testid="product-title">
        {displayH1}
      </h1>
      {sub && !headerOverride && (
        <p className="mt-1 font-mono text-sm font-medium text-subtle sm:text-base" data-testid="product-title-sub">
          {sub}
        </p>
      )}

      {blendComposition && blendComposition.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="blend-composition">
          {blendComposition.map((ing) => (
            <BlendIngredientChip
              key={`${ing.name}-${ing.amount}`}
              ingredient={ing}
            />
          ))}
        </div>
      )}
      <p className="mt-3 text-base text-subtle">{displaySubtitle}</p>
      {(() => {
        // Alternative-format cross-link: for the five compounds that ship
        // in BOTH a Raw Vial and a Pen Kit (GLOW, HULK, KLOW, NAD+,
        // Wolverine), surface the sibling PDP directly beneath the short
        // description so researchers immediately know the other format
        // exists. Rendered on desktop AND mobile, no responsive hiding,
        // no layout shift, no redesign; just an inline link with
        // descriptive anchor text.
        const ALT_FORMAT = {
          glow: { to: "/product/glow-pen", label: "GLOW Pen Kit" },
          "glow-pen": { to: "/product/glow", label: "GLOW Raw Vial" },
          hulk: { to: "/product/hulk-pen", label: "HULK Pen Kit" },
          "hulk-pen": { to: "/product/hulk", label: "HULK Raw Vial" },
          klow: { to: "/product/klow-pen", label: "KLOW Pen Kit" },
          "klow-pen": { to: "/product/klow", label: "KLOW Raw Vial" },
          "nad-plus": { to: "/product/nad-plus-pen", label: "NAD+ Pen Kit" },
          "nad-plus-pen": { to: "/product/nad-plus", label: "NAD+ Raw Vial" },
          wolverine: { to: "/product/wolverine-pen", label: "Wolverine Pen Kit" },
          "wolverine-pen": { to: "/product/wolverine", label: "Wolverine Raw Vial" },
          retatrutide: { to: "/product/retatrutide-pen", label: "Retatrutide Pen Kit" },
          "retatrutide-pen": { to: "/product/retatrutide", label: "Retatrutide Raw Vial" },
        };
        const alt = ALT_FORMAT[product.slug];
        if (!alt) return null;
        return (
          <p className="mt-2 text-sm text-subtle" data-testid="alt-format-link">
            Also available as:{" "}
            <Link
              to={alt.to}
              className="font-medium text-brandblue underline underline-offset-2 hover:text-brandbluedark"
              data-testid="alt-format-anchor"
            >
              {alt.label}
            </Link>
          </p>
        );
      })()}
      <CoaMetaLine product={product} isBlend={isBlend} />

      {product.coming_soon ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="coming-soon-panel">
          <p className="font-mono text-[11px] tracking-[0.18em] text-amber-700">● COMING SOON</p>
          <p className="mt-2 text-sm text-ink">
            This compound is not yet available. Leave your email and we&apos;ll notify you the moment it launches.
          </p>
          <div className="mt-4">
            <NotifyForm productId={product.id} slug={product.slug} context="restock-coming-soon" />
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-xl panel-warm bg-white p-6">
          {strengthVariants.length > 0 && (
            <div className="mb-5" data-testid="strength-selector">
              <p className="label-mono">§ Amount</p>
              <div className={`mt-2 grid gap-2 ${strengthVariants.length >= 3 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"}`}>
                {strengthVariants.map((v, i) => (
                  <button
                    key={v.pack_size}
                    onClick={() => setVariantIdx(i)}
                    aria-pressed={variantIdx === i}
                    className={`rounded-lg border py-2.5 text-center transition-colors ${
                      variantIdx === i ? "border-navy bg-navy text-white" : "btn-outline"
                    }`}
                    data-testid={`strength-option-${v.pack_size}mg`}
                  >
                    <span className="block font-heading text-base font-black">{v.label}</span>
                    <span className={`mt-0.5 block font-mono text-[10px] ${variantIdx === i ? "text-white/80" : "text-subtle"}`}>
                      {fmtPrice(v.price)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="label-mono">§ {strengthVariants.length > 0 ? "Price" : "One kit"}</p>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="font-heading text-4xl font-black text-ink" data-testid="active-price">{fmtPrice(displayedPrice)}</span>
            {!activeStrength && product.original_price && (
              <>
                <span className="text-lg text-subtle line-through">{fmtPrice(product.original_price)}</span>
                <span className="rounded-full bg-brandblue/10 px-2.5 py-1 font-mono text-[11px] font-bold text-brandblue">SAVE {saving}%</span>
              </>
            )}
          </div>

          <p
            className={`mt-3 flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] ${
              stockChip.tone === "oos"
                ? "text-red-600"
                : stockChip.tone === "low"
                ? "text-amber-700"
                : "text-emerald-700"
            }`}
            data-testid="product-stock-indicator"
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                stockChip.tone === "oos"
                  ? "bg-red-500"
                  : stockChip.tone === "low"
                  ? "bg-amber-500"
                  : "bg-emerald-500"
              }`}
              aria-hidden="true"
            />
            {stockChip.text}
          </p>

          {isOOS ? (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5" data-testid="oos-panel">
              <p className="font-mono text-[11px] tracking-[0.18em] text-red-700">● OUT OF STOCK</p>
              <p className="mt-2 text-sm text-ink">
                This SKU is currently sold out. Leave your email and we&apos;ll notify you the moment it&apos;s back.
              </p>
              <div className="mt-4">
                <NotifyForm productId={product.id} slug={product.slug} context="restock" />
              </div>
            </div>
          ) : (
            <>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex items-center rounded-full border border-line">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-ink hover:text-brandblue" data-testid="qty-decrease">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
                  <button
                    onClick={() => setQty((q) => (activeStock === null ? q + 1 : Math.min(activeStock, q + 1)))}
                    disabled={!canIncrease}
                    className="px-3 py-2 text-ink hover:text-brandblue disabled:cursor-not-allowed disabled:text-line disabled:hover:text-line"
                    data-testid="qty-increase"
                    aria-label={canIncrease ? "Increase quantity" : "Reached available stock"}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <button onClick={addToCart} className="btn-pill flex-1 bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark" data-testid="add-to-cart-button">
                  Add to cart →
                </button>
              </div>
              {!canIncrease && (
                <p className="mt-2 font-mono text-[10px] tracking-[0.14em] text-amber-700" data-testid="stock-cap-note">
                  MAX AVAILABLE, {activeStock} IN STOCK
                </p>
              )}
              <button onClick={buyNow} className="btn-pill btn-outline mt-3 w-full py-3 text-sm font-semibold" data-testid="buy-now-button">
                Buy now →
              </button>
            </>
          )}
        </div>
      )}

      {product.format === "raw" && (
        <>
          <AddonToggle
            addonKey="recon_kit"
            testIdPrefix="addon-recon-kit"
            blurb="Bacteriostatic water + insulin syringes + alcohol wipes. Ships in the same box."
            onChange={setReconAddon}
          />
          <AddonToggle
            addonKey="sharps_bin"
            testIdPrefix="addon-sharps-bin"
            blurb="Puncture-resistant container for used needles and syringes, BS7320:1990 compliant."
            onChange={setSharpsAddon}
          />
        </>
      )}
      {product.format === "pen" && (
        <AddonToggle
          addonKey="travel_case"
          testIdPrefix="addon-travel-case"
          blurb="Maintains 2–8°C cold chain during transit, pen + refill capacity."
          onChange={setTravelCaseAddon}
        />
      )}

      {product.format === "vial_kit" && Array.isArray(product.kit_contents) && (
        <div
          className="mt-6 rounded-xl border border-brandblue/20 bg-brandbluelight/60 p-5"
          data-testid="vial-kit-contents"
        >
          <p className="font-mono text-[11px] tracking-[0.18em] text-brandblue">
            § WHAT&apos;S INCLUDED
          </p>
          <ul className="mt-3 space-y-2 text-sm text-ink">
            {product.kit_contents.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brandblue" strokeWidth={2.5} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <VialKitAccessoryToggle />
        </div>
      )}

      <Accordion type="single" collapsible className="mt-6">
        {[
          { t: "Shipping", c: "Dispatched next business day from our UK/EU warehouse, delivered within 1-2 business days. Cold-chain at 2–8°C with full tracking. UK £5.99 · EU £12.90." },
          { t: "Lab certificates", c: "Tested by Janoshik Analytical, HPLC purity (≥99.5%) plus a separate pH test. Both PDFs are published here before purchase and ship in the box." },
          ...(product.format === "vial_kit"
            ? [{
                t: "Stability & storage",
                c: product.stability_note ||
                  "Refrigerate at 2–8°C on arrival. Once opened, use within 30 days. Do not refreeze after reconstitution, protein activity degrades with each freeze-thaw cycle.",
              }]
            : []),
          { t: "Research use", c: "For laboratory research use only. Not for human or veterinary consumption." },
          { t: "Refunds & guarantees", c: "Same-day replacement on damaged or lost shipments. Full refund alternative on request. Pre-dispatch cancellation available anytime." },
        ].map((s, i) => (
          <AccordionItem key={s.t} value={`acc-${i}`} className="border-line">
            <AccordionTrigger className="font-heading text-sm font-bold text-ink hover:text-brandblue" data-testid={`accordion-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {s.t}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-subtle">{s.c}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <Link
        to="/verify"
        className="mt-5 inline-block font-mono text-[11px] tracking-[0.18em] text-navy hover:text-brandblue"
        data-testid="verify-product-link"
      >
        ● VERIFY YOUR PRODUCT →
      </Link>
    </div>
  );
}


/**
 * Renders the CAS / LOT + independent-testing status underneath the
 * product title.
 *
 * Layout (from 2026-08-28 UX spec):
 *   Line 1: CAS xxx · LOT TM-yyyy   (secondary, muted)
 *   Line 2: [icon] status text →    (primary, brand-blue, semibold)
 *
 * Copy rules:
 *   * Never show a purity percentage unless a completed independent COA
 *     backs it — the endpoint enforces this by only exposing `purity`
 *     on `tested` + published records.
 *   * "VIEW COA →" (tested) links straight to the report PDF /
 *     verification URL. Pending / in-progress link to the batch entry
 *     in the public library (`/coa#lot-<n>` or `#pending-<slug>`).
 */
function CoaMetaLine({ product, isBlend }) {
  const { data } = useQuery({
    queryKey: ["coa", product.slug],
    queryFn: async () => (await api.get(`/coa/product/${product.slug}`)).data,
    staleTime: 60_000,
  });
  const current = data?.current;
  const cas = !isBlend && product.cas_number ? `CAS ${product.cas_number}` : null;
  const lot = current?.lot_number || product.lot;

  // Metadata line (CAS + LOT) stays as visually-secondary technical detail.
  const metaBits = [cas, lot ? `LOT ${lot}` : null].filter(Boolean);

  // Testing status line — primary emphasis, one line, one link.
  let statusEl = null;
  if (current) {
    const libraryAnchor = current.lot_number
      ? `/coa#lot-${current.lot_number}`
      : `/coa#pending-${product.slug}`;
    if (current.testing_status === "tested") {
      const reportHref = current.coa_file_url || current.verification_url || libraryAnchor;
      const external = reportHref.startsWith("http");
      const linkProps = external
        ? { href: reportHref, target: "_blank", rel: "noopener noreferrer" }
        : { to: reportHref };
      const Cmp = external ? "a" : Link;
      statusEl = (
        <Cmp
          {...linkProps}
          className="inline-flex items-center gap-1.5 font-mono text-[13px] font-semibold tracking-[0.06em] text-brandblue hover:underline"
          data-testid="pdp-view-coa"
        >
          <span aria-hidden="true" className="text-brandblue">✓</span>
          INDEPENDENTLY TESTED · VIEW COA →
        </Cmp>
      );
    } else if (current.testing_status === "in_progress") {
      statusEl = (
        <Link
          to={libraryAnchor}
          className="inline-flex items-center gap-1.5 font-mono text-[13px] font-semibold tracking-[0.06em] text-brandblue hover:underline"
          data-testid="pdp-testing-in-progress"
        >
          <span aria-hidden="true">◷</span>
          INDEPENDENT TESTING IN PROGRESS →
        </Link>
      );
    } else {
      statusEl = (
        <Link
          to={libraryAnchor}
          className="inline-flex items-center gap-1.5 font-mono text-[13px] font-semibold tracking-[0.06em] text-brandblue hover:underline"
          data-testid="pdp-coa-pending"
        >
          <span aria-hidden="true">◷</span>
          INDEPENDENT COA PENDING →
        </Link>
      );
    }
  }

  return (
    <div className="mt-2" data-testid="product-coa-block">
      <p className="font-mono text-[11px] tracking-[0.12em] text-subtle" data-testid="product-cas-line">
        {metaBits.join(" · ")}
      </p>
      {statusEl && <div className="mt-1.5">{statusEl}</div>}
    </div>
  );
}
